import {
  colorsTuple,
  createTheme,
  defaultVariantColorsResolver,
  type CSSVariablesResolver,
  type VariantColorsResolver,
} from "@mantine/core";
import { alpha, mix, radius, radiusSm, token } from "./tokens";

/**
 * The ink on a filled control, which Mantine would otherwise paint white.
 *
 * White on `token.accent` is 2:1 and on `token.danger` is worse -- both are the failure the
 * palette already has an answer to. `onAccent` and `onDanger` are dark tints of the fill they
 * sit on rather than plain black, and are the lightest such tints that still clear 4.5:1; the
 * contrast block in `tokens.test.ts` is what holds them there.
 */
const variantColorResolver: VariantColorsResolver = (input) => {
  const resolved = defaultVariantColorsResolver(input);
  if (input.variant !== "filled") return resolved;
  if (input.color === "accent") return { ...resolved, color: token.onAccent };
  if (input.color === "danger") return { ...resolved, color: token.onDanger };
  if (input.color === "star") return { ...resolved, color: token.onStar };
  return resolved;
};

/**
 * The site's typeface, named once.
 *
 * `headings.fontFamily` has to be set from the same constant: left alone it falls back to
 * `--mantine-font-family-headings`, which is a different stack, so every `Title` would render
 * in a typeface no other element on the page uses.
 */
export const FONT_STACK = 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif';

/**
 * The one place Mantine learns the palette.
 *
 * Every value derives from `tokens.ts`, which stays the palette's single source of truth --
 * the canvas reads it directly and cannot read a CSS custom property, and `tokens.test.ts`
 * forbids a colour literal in any source file but that one. So no hex is written here either.
 *
 * `colorsTuple` fills all ten required shades from one hex, which is the honest shape of a
 * palette with one accent, one danger and one admin hue and no ramps under them.
 */
export const mantineTheme = createTheme({
  fontFamily: FONT_STACK,
  headings: { fontFamily: FONT_STACK },
  // `body` is `14px/1.5`; Mantine's own `md` leading is 1.55, so every converted paragraph
  // would gain ~3% over the markup it replaced. Pinned flat rather than per-size because the
  // app has exactly one leading.
  lineHeights: { xs: "1.5", sm: "1.5", md: "1.5", lg: "1.5", xl: "1.5" },
  /**
   * The four thresholds the layout answers to. Mantine's own key names, because
   * `mergeMantineTheme` deep-merges: custom names would be *added* to `xs`..`xl` rather than
   * replacing them, leaving every Mantine default beside our own. What each one means:
   *
   *   xs  the window is too short to stack header, player, list and timeline
   *   sm  one column, tab strip instead of list-beside-chat
   *   md  the station name in the listener count stops earning its width
   *   lg  chat drops below the player
   *
   * Mantine's `xl` (90em) still survives the merge; nothing in this app reads it, and nothing
   * here declares it, because the header control that used it no longer exists.
   *
   * `em` and not `px` because `parse-style-props/sort-media-queries.mjs` orders a responsive
   * style prop's media blocks with `Number(q.replace("(min-width: ", "").replace("em)", ""))`;
   * `px` leaves `NaN` and the blocks fall back to insertion order. At a 16px root each value is
   * exactly the pixel number it replaced.
   *
   * `xs` is read as a viewport *height* by `useLayout`. Every Mantine consumer reads breakpoints
   * as widths, so nothing else may use it -- it lives here so the app has one table, not two.
   */
  breakpoints: {
    xs: "35em", // 560px
    sm: "43.75em", // 700px
    md: "62.5em", // 1000px
    lg: "75em", // 1200px
  },
  /**
   * `env()` has no style prop, and a theme value is where Mantine puts a number that props then
   * read by name. `index.html` sets `viewport-fit=cover`, so on a notched phone in landscape the
   * rounded corners and the camera cutout are ours to clear.
   */
  spacing: {
    safeLeft: "var(--android-safe-area-inset-left, env(safe-area-inset-left))",
    safeRight: "var(--android-safe-area-inset-right, env(safe-area-inset-right))",
    safeBottom: "var(--android-safe-area-inset-bottom, env(safe-area-inset-bottom))",
    headerTop:
      "calc(0.625rem + var(--android-status-bar-height, var(--android-safe-area-inset-top, env(safe-area-inset-top))))",
    headerTopPhone: "var(--android-safe-area-inset-top, calc(0.625rem + env(safe-area-inset-top)))",
  },
  white: token.white,
  black: token.black,
  primaryColor: "accent",
  defaultRadius: "sm",
  radius: { sm: radiusSm, md: radius },
  variantColorResolver,
  colors: {
    accent: colorsTuple(token.accent),
    // Selection, and the "on" state of a chip. A fifth of the old alpha: 0.35 was tuned to
    // register against white and is a floodlight over a dark card.
    accentDim: colorsTuple(alpha(token.accent, 0.18)),
    danger: colorsTuple(token.danger),
    warn: colorsTuple(token.warn),
    admin: colorsTuple(token.admin),
    adminDim: colorsTuple(alpha(token.admin, 0.14)),
    jukebox: colorsTuple(token.jukebox),
    unheard: colorsTuple(token.unheard),
    // The list's "you have not played this" wash. Quieter than `accentDim` on purpose:
    // selection marks one row and has to win, while this can be on every row on the page.
    unheardDim: colorsTuple(alpha(token.unheard, 0.13)),
    // Not palette hues: the recessed surface a badge sits on, and the starred fill. Both are
    // registered so `Badge` can name them, since a Mantine default shade would be a second
    // hex outside the token set.
    sunken: colorsTuple(token.bgSunken),
    star: colorsTuple(token.star),
  },
  components: {
    Button: {
      defaultProps: { size: "compact-sm", variant: "default", radius: "sm" },
      // `UnstyledButton` already sets this; `Button` and `ActionIcon` do not, and every
      // tappable control in the app is one of the three. Not gated on pointer type: the
      // property is inert on a mouse, so the media query it came from bought nothing.
      styles: { root: { WebkitTapHighlightColor: "transparent" } },
    },
    ActionIcon: {
      defaultProps: { variant: "subtle", color: "gray", radius: "sm" },
      styles: { root: { WebkitTapHighlightColor: "transparent" } },
    },
    // `lockScroll: false` is load-bearing: Mantine's scroll lock puts `overflow: hidden` plus
    // scrollbar-width padding on `body`, which would change the timeline canvas's measured
    // width the moment a dialog opened. It also matches what the hand-built modals did.
    Modal: {
      defaultProps: { centered: true, radius: "md", size: 360, lockScroll: false },
      // A modal's content is a `Paper`, so it takes the raised surface below; its header is
      // not, and Mantine paints that one `--mantine-color-body`. Two surfaces in one card
      // draws a seam under the title. The header stays opaque because it is sticky.
      styles: { header: { background: "var(--mantine-color-default)" } },
    },
    Popover: {
      defaultProps: {
        position: "bottom-end",
        shadow: "md",
        radius: "md",
        withinPortal: true,
        middlewares: { shift: { crossAxis: true, padding: 8 } },
      },
      styles: {
        dropdown: {
          maxWidth: "calc(100vw - 16px)",
          maxHeight: "calc(100dvh - 16px)",
          overflowY: "auto",
        },
      },
    },
    Menu: {
      defaultProps: { position: "bottom-end", shadow: "md", radius: "md", withinPortal: true },
    },
    Select: { defaultProps: { allowDeselect: false, checkIconPosition: "right", size: "sm" } },
    TextInput: { defaultProps: { size: "sm" } },
    NumberInput: { defaultProps: { size: "sm" } },
    PasswordInput: { defaultProps: { size: "sm" } },
    Textarea: { defaultProps: { size: "sm" } },
    Checkbox: { defaultProps: { size: "sm", iconColor: token.onAccent } },
    Table: { defaultProps: { verticalSpacing: "xs", horizontalSpacing: "sm", fz: "sm" } },
    // `tt: "none"` unconditionally: pill copy here is a station name or a count, never a
    // shout, and Mantine uppercases by default. `fw` for the same reason -- a badge is the
    // smallest text on the page, where any extra ink reads as a shout, and Mantine reaches
    // for `--mantine-font-weight-bold` here rather than `-medium`.
    Badge: { defaultProps: { variant: "light", radius: "xl", tt: "none", fw: 400 } },
    Accordion: { defaultProps: { chevronPosition: "left", variant: "default" } },
    SegmentedControl: {
      defaultProps: { size: "xs", radius: "sm", color: "accent" },
      // The ink on the chosen segment. Mantine never writes `--sc-label-color`; it only
      // *reads* it, with `var(--mantine-color-white)` as the fallback -- which is 2:1 on the
      // accent. The palette already names the colour that belongs on an accent fill, and this
      // is the same fact `variantColorResolver` states above, reached the other way Mantine
      // leaves open.
      vars: () => ({ root: { "--sc-label-color": token.onAccent } }),
    },
    Slider: { defaultProps: { size: "sm", color: "accent" } },
    // Every panel in the app is a `Paper`, and every one of them draws the same edge, so the
    // border belongs here rather than in each panel's `style`. `--paper-border-color` because
    // Mantine's own default for it is `dark-4` -- a different line from the one the header,
    // the timeline and every row separator draw.
    Paper: {
      defaultProps: { bg: "var(--mantine-color-default)", radius: "md", withBorder: true },
      vars: () => ({ root: { "--paper-border-color": "var(--mantine-color-default-border)" } }),
    },
    ScrollArea: { defaultProps: { type: "scroll", scrollbarSize: 6 } },
  },
});

/**
 * Mantine's surface variables, and its two font weights, pointed at ours.
 *
 * A resolver rather than a hand-written `:root` block: Mantine injects its own variables
 * through a runtime `<style>` on `:root, :host`, which any sheet of ours would have to beat on
 * order -- and there is no sheet.
 *
 * The weights are scheme-independent, so they go in `variables`. Mantine writes
 * `font-weight: var(--mantine-font-weight-medium)` on `Button`, `Menu.Item`, `Tabs.Tab`, table
 * headers and the rest, and `-bold` on `Badge` and `kbd`; 600 and 700 were tuned for a light
 * page and over this one's palette every control in the chrome came out shouting -- and 500
 * still did. Chrome copy is a label, so it is regular; the one step up is left for the places
 * that actually rank something. One knob here rather than `fw` at sixty call sites, which is
 * what left them disagreeing.
 *
 * Headings are untouched: they read `--mantine-heading-font-weight` and `--mantine-hN-...`,
 * which are separate variables and are meant to be heavy.
 */
export const mantineCssVariables: CSSVariablesResolver = () => ({
  variables: {
    "--mantine-font-weight-medium": "400",
    "--mantine-font-weight-bold": "500",
  },
  light: {},
  dark: {
    "--mantine-color-body": token.bg,
    "--mantine-color-text": token.text,
    "--mantine-color-dimmed": token.textDim,
    "--mantine-color-default": token.bgRaised,
    "--mantine-color-default-hover": mix(alpha(token.white, 0.05), token.bgRaised),
    "--mantine-color-default-color": token.text,
    "--mantine-color-default-border": alpha(token.white, 0.12),
    "--mantine-color-placeholder": token.textDim,
    "--mantine-color-error": token.danger,
    "--mantine-color-anchor": token.accent,
  },
});
